// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Icon,
  Card,
  Input,
  useToast,
  Badge,
  Avatar,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MdArrowBack, MdSend } from "react-icons/md";
import { formatDate } from "utils/format";
import supportService from "services/supportService";

// Normalize message for display: { id, sender, content, created_at, read_at }
function normalizeMessage(msg) {
  if (!msg) return null;
  return {
    id: msg.id,
    sender: msg.sender || (msg.sender_type === "admin" ? "admin" : "visitor"),
    content: msg.content ?? msg.body ?? "",
    created_at: msg.created_at ?? msg.createdAt,
    read_at: msg.read_at ?? msg.readAt,
  };
}

export default function SupportConversation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
  const headerBg = useColorModeValue("white", "navy.900");
  const headerBorder = useColorModeValue("gray.100", "whiteAlpha.100");
  const visitorBg = useColorModeValue("gray.100", "whiteAlpha.200");
  const visitorBorder = useColorModeValue("gray.200", "whiteAlpha.300");
  const adminBg = useColorModeValue("brand.500", "brand.600");
  const adminColor = "white";
  const inputBg = useColorModeValue("white", "whiteAlpha.100");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const messagesAreaBg = useColorModeValue("gray.50", "navy.900");
  const messageShadow = useColorModeValue("sm", "md");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [visitorTyping, setVisitorTyping] = useState(false);
  const [wsError, setWsError] = useState(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load conversation with history
  useEffect(() => {
    if (!id) {
      setError("Conversation ID is required");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await supportService.getConversation(id);
        if (cancelled) return;
        if (result.success && result.data) {
          setConversation(result.data);
          const list = result.data.messages ?? result.data.message ?? [];
          const normalized = (Array.isArray(list) ? list : []).map(normalizeMessage).filter(Boolean);
          setMessages(normalized);
        } else {
          setError(result.error || "Failed to load conversation");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching conversation:", err);
          setError("Failed to load conversation. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id]);

  // Scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // WebSocket: connect and handle messages
  useEffect(() => {
    if (!id || !conversation) return;

    const wsUrl = supportService.getSupportWebSocketUrl(id);
    if (!wsUrl) {
      setWsError("Could not build WebSocket URL");
      return;
    }

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => setWsConnected(false);
      ws.onerror = () => setWsError("WebSocket error");

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          const type = payload.type;

          if (type === "message" && payload.message) {
            const m = normalizeMessage(payload.message);
            if (m) setMessages((prev) => [...prev, m]);
            return;
          }
          if (type === "typing") {
            const who = payload.who;
            const isTyping = payload.is_typing === true;
            if (who === "visitor") setVisitorTyping(isTyping);
            return;
          }
          if (type === "read" || type === "online") {
            // Optional: update read/online state
            return;
          }
          if (type === "error") {
            setWsError(payload.message || "Error");
            toast({
              title: "Support",
              description: payload.message || "An error occurred",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        } catch (e) {
          console.warn("Support WS parse error:", e);
        }
      };
    } catch (e) {
      setWsError("Failed to connect WebSocket");
      console.error(e);
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnected(false);
      setVisitorTyping(false);
      setWsError(null);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [id, conversation, toast]);

  const sendMessage = async () => {
    const content = (inputValue || "").trim();
    if (!content || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    setSending(true);
    setInputValue("");
    try {
      wsRef.current.send(JSON.stringify({ type: "send_message", content }));
    } catch (e) {
      toast({
        title: "Error",
        description: "Failed to send message",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  const sendTyping = useCallback((isTyping) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    try {
      wsRef.current.send(JSON.stringify({ type: "typing", is_typing: isTyping }));
    } catch (_) {}
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    sendTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(false);
      typingTimeoutRef.current = null;
    }, 1500);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="15px" mb="4">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
        <Button leftIcon={<Icon as={MdArrowBack} />} onClick={() => navigate("/admin/support")}>
          Back to Support
        </Button>
      </Box>
    );
  }

  const email = conversation?.email ?? "Visitor";

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxW="920px" mx="auto" px={{ base: 2, md: 4 }}>
      {wsError && (
        <Alert status="warning" borderRadius="xl" mb="4" size="sm" boxShadow="sm">
          <AlertIcon />
          <Text color={textColor}>{wsError}</Text>
        </Alert>
      )}

      <Card
        p="0"
        bg={cardBg}
        overflow="hidden"
        borderRadius="2xl"
        boxShadow={{ base: "sm", md: "xl" }}
        borderWidth="1px"
        borderColor={borderColor}
      >
        {/* Header */}
        <Flex
          align="center"
          gap="4"
          px={{ base: 4, md: 6 }}
          py="4"
          bg={headerBg}
          borderBottomWidth="1px"
          borderColor={headerBorder}
        >
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Icon as={MdArrowBack} />}
            onClick={() => navigate("/admin/support")}
            color={textColor}
            borderRadius="lg"
          >
            Back
          </Button>
          <Avatar size="md" name={email} bg="brand.400" color="white" flexShrink={0} />
          <Box flex="1" minW="0">
            <Text color={textColor} fontSize="md" fontWeight="700" noOfLines={1}>
              {email}
            </Text>
            <Text color={textColorSecondary} fontSize="xs" mt="0.5">
              {wsConnected ? "Live • You can reply" : "Connecting…"}
            </Text>
          </Box>
          <Badge
            colorScheme={wsConnected ? "green" : "gray"}
            variant="solid"
            fontSize="xs"
            px="3"
            py="1"
            borderRadius="full"
          >
            {wsConnected ? "Live" : "Offline"}
          </Badge>
        </Flex>

        {/* Messages */}
        <Box
          flex="1"
          overflowY="auto"
          px={{ base: 4, md: 6 }}
          py="5"
          minH="360px"
          maxH="58vh"
          display="flex"
          flexDirection="column"
          gap="4"
          bg={messagesAreaBg}
        >
          {messages.length === 0 && !visitorTyping && (
            <Flex flex="1" align="center" justify="center" py="14">
              <Box textAlign="center">
                <Flex
                  w="14"
                  h="14"
                  borderRadius="full"
                  bg={visitorBg}
                  align="center"
                  justify="center"
                  mx="auto"
                  mb="3"
                >
                  <Icon as={MdSend} color={textColorSecondary} boxSize="6" />
                </Flex>
                <Text color={textColorSecondary} fontSize="sm">
                  No messages yet. Type below to reply.
                </Text>
              </Box>
            </Flex>
          )}
          {messages.map((m) => (
            <Flex
              key={m.id}
              justify={m.sender === "admin" ? "flex-end" : "flex-start"}
              align="flex-end"
              gap="2"
            >
              {m.sender === "visitor" && (
                <Avatar size="sm" name={email} bg="gray.400" flexShrink={0} visibility={{ base: "hidden", md: "visible" }} />
              )}
              <Box
                maxW="78%"
                px="4"
                py="3"
                borderRadius="2xl"
                bg={m.sender === "admin" ? adminBg : visitorBg}
                color={m.sender === "admin" ? adminColor : textColor}
                borderWidth={m.sender === "visitor" ? "1px" : 0}
                borderColor={visitorBorder}
                boxShadow={messageShadow}
              >
                <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word" lineHeight="tall">
                  {m.content}
                </Text>
                <Text opacity={0.85} fontSize="xs" mt="2">
                  {m.created_at ? formatDate(m.created_at) : ""}
                </Text>
              </Box>
              {m.sender === "admin" && (
                <Avatar size="sm" name="You" bg="brand.400" flexShrink={0} visibility={{ base: "hidden", md: "visible" }} />
              )}
            </Flex>
          ))}
          {visitorTyping && (
            <Flex justify="flex-start" align="center" gap="2">
              <Avatar size="sm" name={email} bg="gray.400" />
              <Box
                px="4"
                py="2.5"
                borderRadius="2xl"
                bg={visitorBg}
                borderWidth="1px"
                borderColor={visitorBorder}
                boxShadow="sm"
              >
                <Text color={textColorSecondary} fontSize="sm" fontStyle="italic">
                  typing…
                </Text>
              </Box>
            </Flex>
          )}
          <Box ref={messagesEndRef} />
        </Box>

        {/* Reply area */}
        <Box px={{ base: 4, md: 6 }} py="4" bg={cardBg} borderTopWidth="1px" borderColor={borderColor}>
          <Flex gap="3" align="center">
            <Input
              placeholder="Type your reply…"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              isDisabled={!wsConnected}
              color={textColor}
              bg={inputBg}
              borderWidth="1px"
              borderColor={inputBorder}
              borderRadius="xl"
              py="6"
              _placeholder={{ color: "gray.500" }}
              _focus={{ borderColor: "brand.400", boxShadow: "0 0 0 1px var(--chakra-colors-brand-400)" }}
            />
            <Button
              leftIcon={<Icon as={MdSend} />}
              colorScheme="brand"
              onClick={sendMessage}
              isLoading={sending}
              isDisabled={!inputValue.trim() || !wsConnected}
              borderRadius="xl"
              px="5"
              py="6"
              fontWeight="600"
            >
              Reply
            </Button>
          </Flex>
        </Box>
      </Card>
    </Box>
  );
}
